import { CheckRounded, EditOutlined } from "@mui/icons-material";
import { Box, Stack, Button, Skeleton, IconButton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSkeleton,
  isWhitespace,
  MaterialSymbolIcon,
  MaterialSymbols,
  TextEditor,
  TextViewer,
  type TextEditorRef,
} from "src/components";
import { useSession } from "src/hooks";
import { IssueDto, useEditIssueImpact } from "src/http";

interface Props {
  issue?: IssueDto;
  isLoading: boolean;
}

const IssueImpact = (props: Props) => {
  const user = useSession();
  const { mutate: editIssueImpact, isLoading: isSavingImpact } = useEditIssueImpact();
  const [isEditing, setIsEditing] = useState(false);
  const [impact, setImpact] = useState<string | undefined>(undefined);
  const editorRef = useRef<TextEditorRef | null>(null);
  const issueHasImpact = !isWhitespace(props.issue?.impact?.value ?? "");

  useEffect(() => {
    if (isEditing) {
      editorRef.current?.focus();
    }
  }, [isEditing]);

  function handleSaveClicked() {
    if (!props.issue) {
      return;
    }

    editIssueImpact({
      issueGuid: props.issue.guid,
      impact: impact ?? props.issue.impact?.value ?? "",
      teamSlug: props.issue.assignedTeam?.slug,
      createdByUserId: props.issue.createdByUser.userId,
      modifiedByUser: {
        userId: user.userId,
        displayName: user.displayName,
        enabled: true,
        profilePhotoSmallUrl: undefined,
      },
    });

    setImpact(undefined);
    setIsEditing(false);
  }

  function handleCancelClicked() {
    setImpact(undefined);
    setIsEditing(false);
  }

  return props.isLoading || props.issue === undefined ? (
    <AccordionSkeleton renderSkeleton={() => <Skeleton variant="rectangular" height={24} />} />
  ) : (
    <Accordion
      title={(expanded) => (
        <Stack direction="row" spacing={0.5} sx={{
          alignItems: "center"
        }}>
          <MaterialSymbolIcon symbol={MaterialSymbols.Bolt} sx={{ color: "text.secondary" }} />
          <Typography variant="label">Impact</Typography>
          {issueHasImpact && !expanded && <CheckRounded sx={{ color: "text.secondary" }} />}
        </Stack>
      )}
      initialExpanded={issueHasImpact}
      accordionDetailsSx={{ px: 0.5, pb: 0.5 }}
    >
      <Box sx={{ display: "flex" }}>
        {isEditing ? (
          <Stack spacing={0.5} sx={{ flex: 1, zIndex: 2 }}>
            <TextEditor
              ref={editorRef}
              initialValue={impact ?? props.issue?.impact?.value ?? ""}
              onChange={(value) => setImpact(value)}
            />
            <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" onClick={handleCancelClicked}>
                Cancel
              </Button>
              <Button variant="tertiary" disabled={isSavingImpact} onClick={handleSaveClicked}>
                Save
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ flex: 1, pl: 0.5 }}>
            <Stack spacing={0.25} sx={{ flex: 1 }}>
              <TextViewer placeholder={"No impact has been described."} value={props.issue.impact?.value ?? ""} />
              {props.issue.impact && (
                <Typography variant="caption">
                  Last updated by {props.issue.impact?.modifiedByUser.displayName} on{" "}
                  {DateTime.fromISO(props.issue.impact?.modifiedDateTimeUtc ?? "", { zone: "utc" })
                    .toLocal()
                    .toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
              )}
            </Stack>

            {props.issue.permissions.userCanEditImpact && (
              <IconButton size="small" sx={{ alignSelf: "start" }} onClick={() => setIsEditing(true)}>
                <EditOutlined />
              </IconButton>
            )}
          </Stack>
        )}
      </Box>
    </Accordion>
  );
};

export { IssueImpact };
